import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, Save, RefreshCw, AlertTriangle, X, Info } from "lucide-react";
import { API } from "../api";
import { JobEventLog } from "../components/JobEventLog";
import { AuthorizationServerField } from "../components/AuthorizationServerField";
import { CategoryDropdown } from "../components/CategoryDropdown";
import { DataThemesSection } from "../components/DataThemesSection";
import { MultiStateSelector } from "../components/MultiStateSelector";
import { CollectionCategoryPicker } from "../components/CollectionCategoryPicker";
import { CatalogName, ResultOverlay } from "../components/Modal";
import { CARD, Logo } from "../components/PageChrome";
import { accessRights, creatorTypes, euDataThemes } from "../constants";
import { autoCategoriesOf, collectionCategoriesPayload, collectionMissingCategory } from "../lib/collections";
import { apiErrorMessage } from "../lib/format";
import { fetchStates } from "../lib/hooks";

function Field({ label, hint, tooltip, children }) {
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between gap-2">
        <label className="block text-[11px] font-black uppercase tracking-widest text-slate-500">
          {label}
        </label>
        {tooltip && (
          <div className="group relative">
            <button type="button" className="cursor-help">
              <Info size={15} className="text-amber-300 hover:text-amber-600 transition-colors" />
            </button>
            <div className="absolute bottom-full right-0 mb-2 hidden group-hover:block w-64 p-3 bg-slate-900 text-white text-[11px] leading-relaxed rounded-xl shadow-2xl z-50 border border-slate-700 font-medium">
              {tooltip}
              <div className="absolute top-full right-2 border-8 border-transparent border-t-slate-900" />
            </div>
          </div>
        )}
      </div>
      {children}
      {hint && <p className="text-[11px] text-slate-400 font-medium">{hint}</p>}
    </div>
  );
}

function TextInput({ value, onChange, placeholder, type = "text", disabled }) {
  return (
    <input
      type={type}
      value={value || ""}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      disabled={disabled}
      className="w-full px-5 py-4 rounded-2xl bg-slate-50 border-2 border-slate-100 outline-none focus:border-amber-500 focus:bg-white text-sm font-medium text-slate-800 transition-all disabled:opacity-60"
    />
  );
}

function TextArea({ value, onChange, rows = 4, disabled }) {
  return (
    <textarea
      value={value || ""}
      onChange={(e) => onChange(e.target.value)}
      rows={rows}
      disabled={disabled}
      className="w-full px-5 py-4 rounded-2xl bg-slate-50 border-2 border-slate-100 outline-none focus:border-amber-500 focus:bg-white text-sm font-medium text-slate-800 transition-all resize-y disabled:opacity-60"
    />
  );
}

function Select({ value, onChange, options, disabled }) {
  return (
    <select
      value={value || ""}
      onChange={(e) => onChange(e.target.value)}
      disabled={disabled}
      className="w-full px-5 py-4 rounded-2xl bg-slate-50 border-2 border-slate-100 outline-none focus:border-amber-500 focus:bg-white text-sm font-bold text-slate-800 transition-all disabled:opacity-60 cursor-pointer"
    >
      <option value="">Select...</option>
      {options.map(o => (
        <option key={o.value} value={o.value}>{o.label}</option>
      ))}
    </select>
  );
}

function Toggle({ value, onChange, disabled }) {
  // Tri-state: with no value neither side is highlighted, forcing an explicit pick.
  const isYes = value === true || value === "true";
  const isNo = value === false || value === "false";
  return (
    <div className="flex gap-3">
      <button
        type="button"
        disabled={disabled}
        onClick={() => onChange("true")}
        className={`flex-1 py-3 rounded-xl font-black text-xs uppercase tracking-widest border-2 transition-all
          ${isYes ? "bg-amber-600 text-white border-amber-600" : "bg-white text-slate-600 border-slate-200 hover:border-slate-300"}
          disabled:opacity-50`}
      >Yes</button>
      <button
        type="button"
        disabled={disabled}
        onClick={() => onChange("false")}
        className={`flex-1 py-3 rounded-xl font-black text-xs uppercase tracking-widest border-2 transition-all
          ${isNo ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-200 hover:border-slate-300"}
          disabled:opacity-50`}
      >No</button>
    </div>
  );
}

export default function MetadataEditor({ target, onCancel, onDone }) {
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [saving, setSaving] = useState(false);
  const [activeJobId, setActiveJobId] = useState(null);
  const [terminalStatus, setTerminalStatus] = useState(null);
  const [submitError, setSubmitError] = useState("");

  const [form, setForm] = useState({
    title: "", description: "", language: "de",
    endpointURL: "", openapi_url: "", modified: "",
    license: "", termsOfService: "",
    access_right: "", hasCreatorType: "", authorizationServer: "",
    containsSyntheticData: "false",
    isAccessibleWithoutPayment: "false",
    isAccessibleWithoutRegistration: "false",
    providerName: "",
    geometry: "",
    keywords: [],
    category: [],
    dataThemes: [],
    multiStates: [],
  });
  const [keywordsRaw, setKeywordsRaw] = useState("");
  // Whether the OpenAPI document changed; null until the user answers, which saving requires.
  const [openapiChanged, setOpenapiChanged] = useState(null);
  const [collections, setCollections] = useState([]);
  const [collectionsLoading, setCollectionsLoading] = useState(false);
  const [collectionCategories, setCollectionCategories] = useState({});

  const [selectedStates, setSelectedStates] = useState([]);
  // Whether form.geometry was filled from a state pick (and may be cleared again).
  const [geometryFromState, setGeometryFromState] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setLoadError("");
    (async () => {
      try {
        const res = await API.get("/api/datasources/metadata", { params: { iri: target.datasource_iri } });
        if (cancelled) return;
        const d = res.data || {};
        setForm(f => ({
          ...f,
          title: d.title || "",
          description: d.description || "",
          language: d.language || "de",
          endpointURL: d.endpointURL || "",
          openapi_url: d.openapi_url || "",
          modified: d.modified ? String(d.modified).split("T")[0] : "",
          license: d.license || "",
          termsOfService: d.termsOfService || "",
          access_right: d.access_right || "",
          authorizationServer: d.authorizationServer || "",
          hasCreatorType: d.hasCreatorType || "",
          containsSyntheticData: d.containsSyntheticData || "false",
          isAccessibleWithoutPayment: d.isAccessibleWithoutPayment || "false",
          isAccessibleWithoutRegistration: d.isAccessibleWithoutRegistration || "false",
          providerName: d.providerName || "",
          geometry: d.geometry || "",
          keywords: Array.isArray(d.keywords) ? d.keywords : [],
          category: Array.isArray(d.category) ? d.category : [],
          dataThemes: Array.isArray(d.dataThemes) ? d.dataThemes : [],
          multiStates: Array.isArray(d.multiStates) ? d.multiStates : [],
        }));
        setKeywordsRaw((d.keywords || []).join(", "));

        // Hydrate the multi-state picker; without the catalog the user simply re-picks.
        const catalog = await fetchStates();
        if (!cancelled) {
          const codes = Array.isArray(d.multiStates) ? d.multiStates : [];
          setSelectedStates(codes.map(code => catalog.find(s => s.code === code)).filter(Boolean));
        }
      } catch (e) {
        if (cancelled) return;
        setLoadError(e.response?.data?.detail || "Failed to load metadata from the catalog.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [target.datasource_iri]);

  // Collections are only needed when the OpenAPI changed, the one path that re-converts the graph.
  useEffect(() => {
    if (!openapiChanged) {
      setCollections([]);
      setCollectionCategories({});
      return;
    }
    const endpoint = (form.endpointURL || "").trim();
    if (!endpoint) {
      setCollections([]);
      setCollectionCategories({});
      return;
    }
    let cancelled = false;
    const timer = setTimeout(() => {
      setCollectionsLoading(true);
      API.get(`/api/metadata/collections?url=${encodeURIComponent(endpoint)}`)
        .then((res) => {
          if (cancelled) return;
          const cs = Array.isArray(res.data?.collections) ? res.data.collections : [];
          setCollections(cs);
          setCollectionCategories(Object.fromEntries(cs.map(c => [c.id, [...autoCategoriesOf(c)]])));
        })
        .catch((e) => {
          if (cancelled) return;
          console.warn("Collections discovery failed:", e?.response?.data?.detail || e.message);
          setCollections([]);
        })
        .finally(() => { if (!cancelled) setCollectionsLoading(false); });
    }, 600);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [openapiChanged, form.endpointURL]);

  const update = (key, value) => setForm(prev => ({ ...prev, [key]: value }));
  // Fields are frozen while saving and once a job is running.
  const locked = saving || !!activeJobId;

  // One state fills the geometry from its boundary; several clear a geometry that came from a state.
  const handleStatesChange = (next) => {
    setSelectedStates(next);
    if (next.length === 1) {
      const g = next[0].geometry || "";
      if (g) {
        setForm(prev => ({ ...prev, geometry: g }));
        setGeometryFromState(true);
      }
    } else if (next.length > 1) {
      if (geometryFromState) {
        setForm(prev => ({ ...prev, geometry: "" }));
        setGeometryFromState(false);
      }
    }
  };

  const handleGeometryEdit = (val) => {
    update("geometry", val);
    setGeometryFromState(false);
  };

  const submit = async () => {
    setSubmitError("");
    if (openapiChanged === null) {
      setSubmitError("Please select whether the OpenAPI document changed before saving.");
      return;
    }
    // A re-convert needs a category for every collection without an auto-detected one.
    if (openapiChanged) {
      const missing = collectionMissingCategory(collections, collectionCategories);
      if (missing) {
        setSubmitError(`Pick at least one NEED category for collection "${missing.id}".`);
        return;
      }
    }
    setSaving(true);
    const data = new FormData();
    data.append("datasource_iri", target.datasource_iri);
    data.append("title", form.title);
    data.append("description", form.description);
    data.append("language", form.language || "de");
    data.append("endpointURL", form.endpointURL);
    data.append("openapi_url", form.openapi_url);
    data.append("modified", form.modified);
    data.append("license", form.license);
    if (form.termsOfService) data.append("termsOfService", form.termsOfService);
    data.append("providerName", form.providerName);
    data.append("access_right", form.access_right);
    if (form.access_right === "RESTRICTED") data.append("authorizationServer", form.authorizationServer || "");
    data.append("hasCreatorType", form.hasCreatorType);
    data.append("containsSyntheticData", form.containsSyntheticData);
    data.append("isAccessibleWithoutPayment", form.isAccessibleWithoutPayment);
    data.append("isAccessibleWithoutRegistration", form.isAccessibleWithoutRegistration);
    if (form.geometry) data.append("geometry", form.geometry);
    const stateCodes = selectedStates.map(s => s.code);
    data.append("state", stateCodes[0] || "");
    data.append("multiStates", JSON.stringify(stateCodes));
    const kws = (keywordsRaw || "").split(",").map(s => s.trim()).filter(Boolean);
    data.append("keywords", JSON.stringify(kws));
    data.append("category", JSON.stringify(form.category || []));
    data.append("dataThemes", JSON.stringify(form.dataThemes || []));
    data.append("openapiChanged", openapiChanged ? "true" : "false");
    if (openapiChanged) {
      data.append("collectionCategories", JSON.stringify(collectionCategoriesPayload(collections, collectionCategories)));
    }

    try {
      const res = await API.patch("/api/datasources/metadata", data);
      if (res.status === 202 && res.data.job_id) {
        setActiveJobId(res.data.job_id);
      } else {
        setSaving(false);
        setSubmitError("Unexpected response from server.");
      }
    } catch (e) {
      setSaving(false);
      setSubmitError(apiErrorMessage(e, "Failed to save metadata changes."));
    }
  };

  const handleTerminal = ({ status }) => {
    setTerminalStatus(status);
    setSaving(false);
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        className="fixed inset-0 z-[100] bg-slate-200 overflow-y-auto"
      >
        <nav className="sticky top-0 w-full bg-white/80 backdrop-blur-xl border-b border-slate-200/50 z-10 px-10 py-5 flex justify-between items-center">
          <div className="flex items-center gap-3">
            <button
              onClick={onCancel}
              className="p-2 mr-2 rounded-xl hover:bg-slate-100 transition-all"
              title="Back to datasource list"
            >
              <ArrowLeft size={20} className="text-slate-700" />
            </button>
            <Logo accent="amber" label="MODIFY" />
          </div>
        </nav>

        <main className="max-w-4xl mx-auto px-6 py-10">
          {loading && (
            <div className={`${CARD} p-12 text-center`}>
              <RefreshCw size={28} className="mx-auto mb-3 text-amber-500 animate-spin" />
              <p className="text-sm font-bold text-slate-500 uppercase tracking-widest">Loading from RDF4J catalog...</p>
            </div>
          )}

          {loadError && !loading && (
            <div className="bg-red-50 border border-red-100 rounded-3xl p-6 flex items-start gap-3 mb-6">
              <AlertTriangle size={18} className="text-red-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-bold text-red-700">{loadError}</p>
                <button onClick={onCancel} className="text-xs font-black uppercase tracking-widest text-red-700 underline mt-2">
                  Back to list
                </button>
              </div>
            </div>
          )}

          {!loading && !loadError && (
            <div className={`${CARD} p-8 md:p-12 space-y-8`}>
              <header className="text-left">
                <h1 className="text-2xl font-black uppercase text-slate-900 tracking-widest">
                  Modify <span className="text-amber-600">"{target.title || "Untitled"}"</span>
                </h1>
              </header>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Field label="Title">
                  <TextInput value={form.title} onChange={(v) => update("title", v)} disabled={locked} />
                </Field>
                <Field label="Provider Name">
                  <TextInput value={form.providerName} onChange={(v) => update("providerName", v)} disabled={locked} />
                </Field>
              </div>

              <Field label="Description">
                <TextArea value={form.description} onChange={(v) => update("description", v)} disabled={locked} />
              </Field>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Field label="Endpoint URL">
                  <TextInput value={form.endpointURL} onChange={(v) => update("endpointURL", v)} disabled={locked} />
                </Field>
                <Field label="OpenAPI URL" hint="If the OpenAPI document changed, update the URL here and answer 'Yes' below.">
                  <TextInput value={form.openapi_url} onChange={(v) => update("openapi_url", v)} disabled={locked} />
                </Field>
              </div>

              <div className={`space-y-3 rounded-2xl p-5 transition-all
                ${openapiChanged === null && !activeJobId
                  ? "border-2 border-amber-300 bg-amber-50/50 ring-4 ring-amber-100"
                  : "border-2 border-transparent"}`}
              >
                <Field
                  label="Did the OpenAPI document change?"
                  tooltip="Answer 'Yes' if collection names, attribute names, provider details or any other OpenAPI-derived metadata changed. The whole source graph will then be re-converted from the OpenAPI document. 'No' patches only the metadata context, leaving OpenAPI-derived triples untouched."
                >
                  <Toggle value={openapiChanged} onChange={(v) => setOpenapiChanged(v === "true")} disabled={locked} />
                </Field>
                {openapiChanged === null && !activeJobId && (
                  <p className="text-xs font-bold text-amber-700 flex items-center gap-1.5">
                    <AlertTriangle size={13} /> Please choose Yes or No before saving.
                  </p>
                )}
                {openapiChanged && collections.length > 0 && (
                  <div className="pt-2">
                    <label className="block text-[11px] font-black uppercase tracking-widest text-slate-500 mb-2">
                      Collection categories
                    </label>
                    <p className="text-xs text-slate-500 mb-3 leading-relaxed">
                      Every discovered collection is listed. Auto-detected categories are locked; add more as needed,
                      and pick a category for any collection without one. The graph is re-converted on save.
                    </p>
                    <CollectionCategoryPicker
                      collections={collections}
                      value={collectionCategories}
                      onChange={(partial) => setCollectionCategories(prev => ({ ...prev, ...partial }))}
                      loading={collectionsLoading}
                      variant="amber"
                    />
                  </div>
                )}
                {openapiChanged && collectionsLoading && (
                  <p className="text-xs font-semibold text-slate-400 italic pt-1">Discovering collections from the endpoint...</p>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <Field label="Last Modified">
                  <TextInput type="date" value={form.modified} onChange={(v) => update("modified", v)} disabled={locked} />
                </Field>
                <Field label="License (URL)">
                  <TextInput value={form.license} onChange={(v) => update("license", v)} disabled={locked} />
                </Field>
                <Field label="License Attribution Text" >
                  <TextInput value={form.termsOfService} onChange={(v) => update("termsOfService", v)} disabled={locked} placeholder="Optional attribution text..." />
                </Field>
              </div>

              <Field label="Keywords" hint="Comma-separated.">
                <TextInput value={keywordsRaw} onChange={setKeywordsRaw} disabled={locked} />
              </Field>

              <Field label="NEED Categories">
                <CategoryDropdown
                  value={form.category}
                  onChange={(vals) => update("category", vals)}
                  variant="amber"
                />
              </Field>

              <Field label="EU Data Themes">
                <DataThemesSection
                  euDataThemes={euDataThemes}
                  selected={form.dataThemes}
                  onToggle={(code) => {
                    const next = form.dataThemes.includes(code)
                      ? form.dataThemes.filter(c => c !== code)
                      : [...form.dataThemes, code];
                    update("dataThemes", next);
                  }}
                  variant="amber"
                />
              </Field>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Field label="Access Right">
                  <Select
                    value={form.access_right}
                    onChange={(v) => update("access_right", v)}
                    options={accessRights}
                    disabled={locked}
                  />
                </Field>
                <Field label="Creator Type">
                  <Select
                    value={form.hasCreatorType}
                    onChange={(v) => update("hasCreatorType", v)}
                    options={creatorTypes.map(t => ({ value: t, label: t }))}
                    disabled={locked}
                  />
                </Field>
              </div>

              {form.access_right === "RESTRICTED" && (
                <Field label="Authorization Server">
                  <AuthorizationServerField
                    value={form.authorizationServer}
                    onChange={(v) => update("authorizationServer", v)}
                    openapiUrl={form.openapi_url}
                    disabled={locked}
                    inputClassName="w-full px-5 py-4 rounded-2xl bg-slate-50 border-2 border-slate-100 outline-none focus:border-amber-500 focus:bg-white text-sm font-bold text-slate-800 transition-all disabled:opacity-60"
                  />
                </Field>
              )}

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <Field label="Contains Synthetic Data">
                  <Toggle value={form.containsSyntheticData} onChange={(v) => update("containsSyntheticData", v)} disabled={locked} />
                </Field>
                <Field label="Cost-Free Access">
                  <Toggle value={form.isAccessibleWithoutPayment} onChange={(v) => update("isAccessibleWithoutPayment", v)} disabled={locked} />
                </Field>
                <Field label="Registration-Free Access">
                  <Toggle value={form.isAccessibleWithoutRegistration} onChange={(v) => update("isAccessibleWithoutRegistration", v)} disabled={locked} />
                </Field>
              </div>

              <Field
                label="Federal States"
                hint={
                  selectedStates.length > 1
                    ? `${selectedStates.length} states selected — a single WKT geometry can't describe a multi-state region, so geometry is optional below.`
                    : "Pick one state to auto-fill the geometry, or pick several for a multi-state datasource."
                }
              >
                <MultiStateSelector
                  selectedStates={selectedStates}
                  onChange={handleStatesChange}
                  variant="amber"
                />
              </Field>

              <Field
                label="Geometry (WKT)"
                hint={
                  selectedStates.length === 1
                    ? "Auto-filled from the selected state's official boundary. Edit to override with a custom WKT."
                    : selectedStates.length > 1
                      ? "Optional for multi-state datasources. Leave empty unless you have a single custom polygon covering all selected states."
                      : "Optional. Custom polygon as Well-Known Text."
                }
              >
                <TextArea
                  value={form.geometry}
                  onChange={handleGeometryEdit}
                  rows={3}
                  disabled={locked}
                />
              </Field>

              {submitError && (
                <div className="text-sm font-bold text-red-600">{submitError}</div>
              )}

              {activeJobId && (
                <div className="flex justify-center">
                  <JobEventLog jobId={activeJobId} onTerminal={handleTerminal} accent="amber" />
                </div>
              )}

              <div className="flex flex-col md:flex-row gap-3 pt-6 border-t border-slate-100">
                <button
                  onClick={onCancel}
                  disabled={saving && !terminalStatus}
                  className="flex-1 py-5 bg-slate-100 text-slate-700 rounded-2xl font-black text-xs uppercase tracking-widest hover:bg-slate-200 transition-all active:scale-95 disabled:opacity-50 inline-flex items-center justify-center gap-2"
                >
                  <X size={14} /> {terminalStatus ? "Close" : "Cancel"}
                </button>
                {!activeJobId && (
                  <button
                    onClick={submit}
                    disabled={saving || openapiChanged === null}
                    title={openapiChanged === null ? "Answer whether the OpenAPI document changed first" : undefined}
                    className="flex-1 py-5 bg-amber-600 text-white rounded-2xl font-black text-xs uppercase tracking-widest hover:bg-amber-700 transition-all shadow-xl shadow-amber-200 active:scale-95 disabled:opacity-60 disabled:cursor-not-allowed inline-flex items-center justify-center gap-2"
                  >
                    <Save size={14} /> {saving ? "Saving..." : openapiChanged ? "Save & Re-register OpenAPI" : "Save Changes"}
                  </button>
                )}
              </div>
            </div>
          )}
        </main>

        <ResultOverlay isVisible={terminalStatus === "completed"} success z="z-[110]"
                       title="Modification Complete" buttonLabel="Close" onClose={onDone}>
          {openapiChanged
            ? <>The datasource was re-registered from the updated OpenAPI document in the <CatalogName />.</>
            : <>The metadata context was updated in the <CatalogName />.</>}
        </ResultOverlay>
      </motion.div>
    </AnimatePresence>
  );
}
